/**
 * InstallDetailsCard (INSTALL-SPEC-02, Seth 2026-09-25) — the install details,
 * asked in the order a salesperson thinks and tailored to the equipment on the
 * job: an 80% furnace never asks about PVC, no AC means no line set, a 90%+
 * furnace asks 1-pipe or 2-pipe and whether the PVC is reused, replaced (how
 * many feet) or new. Items on the job pre-answer what they can (a thermostat
 * on the job = New thermostat; a line set = New line set).
 *
 * Competitor frame: ServiceTitan's job Forms and Jobber's job forms can show a
 * question when another answer says so, but someone has to build that logic
 * per job type — nobody tailors to the equipment actually sold. Pegasus reads
 * the equipment itself (adopt the conditional form, adapt the trigger).
 *
 * Saves as you go (a chip saves right away, typing saves when you pause) —
 * no Save button to forget. Only the changed answers are written on top of the
 * latest copy, so two people editing different answers don't erase each other.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { AlertCircle, Check, ClipboardList, Eye, EyeOff, Loader2, Package, RotateCcw, Sparkles } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { InfoTip } from "@/components/InfoTip";
import type { Job } from "./jobs";
import {
  applies, effectiveTags, impliedPick, isAnswered, pickOf, specProgress, tailoredTo, useInstallBank,
  type EquipFacts, type SpecQuestion,
} from "./installSpec";

type SaveState = "idle" | "saving" | "saved" | "error";

export function InstallDetailsCard({ job, facts, closed = false, onAddEquipment, variant = "office" }: {
  job: Job;
  facts: EquipFacts;
  closed?: boolean;
  /** Scroll to the equipment card (it sits above this one). */
  onAddEquipment?: () => void;
  /** JOB-SOLD-01: "phone" = the sales tech fills it in the install packet — 44px chips, the phone's look. */
  variant?: "office" | "phone";
}) {
  const phone = variant === "phone";
  const qc = useQueryClient();
  const server = job.install_spec ?? {};
  // Unsaved edits, key → value ("" = cleared). Shown on top of the saved copy
  // until the saved copy catches up, so nothing flickers back while saving.
  const [local, setLocal] = useState<Record<string, string>>({});
  const localRef = useRef<Record<string, string>>({});
  const serverRef = useRef(server);
  serverRef.current = server;
  const timer = useRef<number | null>(null);
  const [saveState, setSaveState] = useState<SaveState>("idle");
  const [showAll, setShowAll] = useState(false);

  // (The page keys this card by job id, so another job starts it fresh.)
  // Drop local edits the saved copy now reflects.
  useEffect(() => {
    const s = job.install_spec ?? {};
    const l = localRef.current;
    const keep: Record<string, string> = {};
    for (const [k, v] of Object.entries(l)) if ((s[k] ?? "") !== v) keep[k] = v;
    if (Object.keys(keep).length !== Object.keys(l).length) { localRef.current = keep; setLocal(keep); }
  }, [job.install_spec]);

  const jobId = job.id;
  const flush = useCallback(async () => {
    if (timer.current) { window.clearTimeout(timer.current); timer.current = null; }
    const patch = localRef.current;
    if (Object.keys(patch).length === 0) return;
    const next: Record<string, string> = { ...serverRef.current };
    for (const [k, v] of Object.entries(patch)) { if (v.trim() === "") delete next[k]; else next[k] = v; }
    setSaveState("saving");
    const { error } = await supabase.from("pegasus_job")
      .update({ install_spec: next, updated_at: new Date().toISOString() }).eq("id", jobId);
    if (error) { setSaveState("error"); return; }
    setSaveState("saved");
    qc.invalidateQueries({ queryKey: ["job"] });
    qc.invalidateQueries({ queryKey: ["jobs"] });
  }, [jobId, qc]);
  // Leaving the page mid-typing still saves.
  useEffect(() => () => { void flush(); }, [flush]);
  useEffect(() => {
    if (saveState !== "saved") return;
    const t = window.setTimeout(() => setSaveState("idle"), 2500);
    return () => window.clearTimeout(t);
  }, [saveState]);

  const set = (k: string, v: string, delay = 400) => {
    localRef.current = { ...localRef.current, [k]: v };
    setLocal(localRef.current);
    if (timer.current) window.clearTimeout(timer.current);
    timer.current = window.setTimeout(() => { void flush(); }, delay);
  };
  const clearMany = (keys: string[]) => {
    const patch = { ...localRef.current };
    for (const k of keys) { patch[k] = ""; patch[`${k}_amt`] = ""; patch[`${k}_note`] = ""; }
    localRef.current = patch;
    setLocal(patch);
    void flush();
  };

  const spec: Record<string, string> = { ...server, ...local };
  // INSTALL-SPEC-ADMIN: the questions come from Settings → Install Details.
  const bank = useInstallBank();
  const prog = specProgress(spec, facts, bank);
  const tags = effectiveTags(facts, spec);
  const noEquipment = facts.lines === 0;
  const labels = tailoredTo(facts, spec);
  const pct = prog.total > 0 ? Math.round((prog.answered / prog.total) * 100) : 0;
  const open = prog.total - prog.answered;

  return (
    <section className={phone
      ? "rounded-3xl border border-slate-200 bg-white shadow-[0_1px_2px_rgba(16,24,40,0.04),0_5px_14px_-7px_rgba(16,24,40,0.14)]"
      : "rounded-xl border border-hairline bg-white"}>
      {/* Header — what it's tailored to, how far along, and the save state. */}
      <header className={cn("flex flex-wrap items-start gap-x-6 gap-y-3 border-b border-slate-100", phone ? "px-4 py-3.5" : "px-5 py-4")}>
        <div className="min-w-0 flex-1">
          <h3 className={cn("flex items-center gap-2 font-semibold text-slate-900", phone ? "text-[15px] font-bold tracking-tight" : "text-lg")}>
            <ClipboardList className={cn("text-brand-500", phone ? "h-4 w-4 text-slate-500" : "h-5 w-5")} /> Installer Guide
            <InfoTip label="About the installer guide">What the crew and the subcontractor need to know on install day. Only the questions that fit the equipment on the job are asked — an 80% furnace skips PVC, no AC skips the line set. The sales tech can fill it in from the phone&apos;s install packet. The questions are set in Settings → Installer Guide.</InfoTip>
          </h3>
          {labels.length > 0 && (
            <div className="mt-2.5 flex flex-wrap items-center gap-1.5">
              <span className="text-[13px] font-medium text-slate-500">Tailored to</span>
              {labels.map((l) => (
                <span key={l} className="rounded-full bg-brand-50 px-2.5 py-0.5 text-[13px] font-semibold text-brand-700 ring-1 ring-inset ring-brand-100">{l}</span>
              ))}
            </div>
          )}
        </div>
        {!noEquipment && prog.total > 0 && (
          <div className={cn("flex flex-col gap-1.5", phone ? "w-full items-stretch" : "w-52 items-end")}>
            <span className={cn("text-[15px] font-bold tabular-nums", prog.complete ? "text-emerald-700" : "text-slate-800")}>
              {prog.complete ? <><Check className="mr-1 inline h-4 w-4 align-[-2px]" strokeWidth={3} />All {prog.total} answered</> : `${prog.answered} of ${prog.total} answered`}
            </span>
            <div className="h-2 w-full overflow-hidden rounded-full bg-slate-100" role="progressbar" aria-valuenow={pct} aria-valuemin={0} aria-valuemax={100} aria-label="Install details answered">
              <div className={cn("h-full rounded-full transition-[width] duration-300", prog.complete ? "bg-emerald-500" : "bg-brand-500")} style={{ width: `${pct}%` }} />
            </div>
            <SaveBadge state={saveState} onRetry={() => void flush()} />
          </div>
        )}
      </header>

      {noEquipment && (
        <div className={cn("mt-4 flex items-start gap-3 rounded-xl bg-amber-50 px-4 py-3 ring-1 ring-inset ring-amber-200", phone ? "mx-4" : "mx-5")}>
          <Package className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
          <div className="text-[15px] leading-relaxed text-amber-900">
            <strong>Add the equipment first</strong> — these questions follow it.
            {onAddEquipment && (
              <button type="button" onClick={onAddEquipment} className="ml-1.5 font-semibold text-amber-800 underline underline-offset-2 hover:text-amber-950">
                Add Equipment
              </button>
            )}
          </div>
        </div>
      )}

      <div className="divide-y divide-slate-100">
        {bank.sections.map((sec) => {
          const all = bank.questions.filter((q) => q.section === sec.key);
          const fits = all.filter((q) => applies(q, tags, spec));
          const shown = showAll ? all : fits;
          if (shown.length === 0) return null;
          const counted = fits.filter((q) => !q.optional);
          const done = counted.filter((q) => isAnswered(q, spec, facts)).length;
          return (
            <div key={sec.key} className={phone ? "px-4 py-3.5" : "px-5 py-4"}>
              <div className="mb-3 flex items-baseline justify-between gap-3">
                <h4 className="text-[15px] font-bold text-slate-800">{sec.title}</h4>
                {counted.length > 0 && (
                  <span className={cn("text-[13px] font-semibold tabular-nums", done === counted.length ? "text-emerald-700" : "text-slate-500")}>
                    {done === counted.length ? "Done" : `${done} of ${counted.length}`}
                  </span>
                )}
              </div>
              <div className="space-y-3.5">
                {shown.map((q) => (
                  <QuestionRow
                    key={q.key}
                    q={q}
                    spec={spec}
                    facts={facts}
                    tags={tags}
                    fits={fits.includes(q)}
                    disabled={closed}
                    onSet={set}
                    onBlur={() => void flush()}
                    phone={phone}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      <footer className={cn("flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-slate-100 py-3", phone ? "px-4" : "px-5")}>
        {prog.stranded.length > 0 ? (
          <span className="flex min-w-0 flex-1 flex-wrap items-center gap-x-2 text-[14px] text-slate-600">
            <AlertCircle className="h-4 w-4 shrink-0 text-amber-600" />
            {prog.stranded.length} earlier answer{prog.stranded.length === 1 ? "" : "s"} no longer fit this equipment and won&apos;t print
            ({prog.stranded.map((q) => q.label).join(", ")}).
            {!closed && (
              <button type="button" onClick={() => clearMany(prog.stranded.map((q) => q.key))} className="font-semibold text-brand-700 hover:underline">
                Clear {prog.stranded.length === 1 ? "it" : "them"}
              </button>
            )}
          </span>
        ) : (
          <span className="min-w-0 flex-1 text-[14px] text-slate-500">
            {noEquipment ? "" : open > 0 ? `${open} still open — the crew and the sub see blanks for these.` : "Everything the crew needs is answered."}
          </span>
        )}
        <button type="button" onClick={() => setShowAll((v) => !v)} className="inline-flex items-center gap-1.5 text-[14px] font-medium text-slate-500 transition-colors hover:text-brand-700">
          {showAll ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
          {showAll ? "Only questions for this equipment" : "Show every question"}
        </button>
      </footer>
    </section>
  );
}

function SaveBadge({ state, onRetry }: { state: SaveState; onRetry: () => void }) {
  if (state === "saving") return <span className="inline-flex items-center gap-1.5 text-[13px] text-slate-500"><Loader2 className="h-3.5 w-3.5 animate-spin" /> Saving…</span>;
  if (state === "saved") return <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-emerald-700"><Check className="h-3.5 w-3.5" strokeWidth={3} /> Saved</span>;
  if (state === "error") return (
    <button type="button" onClick={onRetry} className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-rose-600 hover:underline">
      <RotateCcw className="h-3.5 w-3.5" /> Couldn&apos;t save — retry
    </button>
  );
  return <span className="text-[13px] text-slate-400">Saves as you go</span>;
}

function QuestionRow({ q, spec, facts, tags, fits, disabled, onSet, onBlur, phone = false }: {
  q: SpecQuestion;
  tags: Set<string>;
  spec: Record<string, string>;
  facts: EquipFacts;
  fits: boolean;
  disabled: boolean;
  onSet: (k: string, v: string, delay?: number) => void;
  onBlur: () => void;
  phone?: boolean;
}) {
  const raw = spec[q.key] ?? "";
  const saved = raw.trim();
  const implied = impliedPick(q, facts);
  const pick = pickOf(q, spec, facts);
  const fromEquipment = !saved && !!implied;
  const note = spec[`${q.key}_note`] ?? "";
  const amt = spec[`${q.key}_amt`] ?? "";
  const answered = isAnswered(q, spec, facts);
  const legacyPick = saved && q.options && !q.options.includes(saved) ? saved : "";
  // A pick that's no longer one of the choices keeps its amount box while it has one.
  const needsAmount = !!pick && (!!q.amountFor?.includes(pick) || (!!legacyPick && amt.trim() !== ""));
  const showNote = !!q.note && !q.text && (!!pick || note.trim() !== "");
  // The salesperson's pick disagrees with the furnace on the job.
  const effClash = q.key === "furnace_eff" && !!saved && !!facts.furnaceEffGuess && !saved.startsWith(facts.furnaceEffGuess === "80" ? "80" : "90");
  const inputCls = phone
    ? "h-11 rounded-xl border border-slate-200 bg-white px-3 text-[16px] text-slate-800 placeholder:text-slate-400 focus:border-red-500 focus:outline-none focus:ring-2 focus:ring-red-500/25 disabled:bg-slate-50"
    : "h-9 rounded-lg border border-slate-200 bg-white px-3 text-[15px] text-slate-800 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/25 disabled:bg-slate-50";

  return (
    <div className={cn("grid gap-x-5 gap-y-1.5", !phone && "md:grid-cols-[220px_minmax(0,1fr)]", !fits && "opacity-60")}>
      <div className="md:pt-1.5">
        <div className="flex items-center gap-1.5 text-[15px] font-semibold leading-snug text-slate-700">
          {!answered && !q.optional && fits && <span className="h-2 w-2 shrink-0 rounded-full bg-amber-400" title="Not answered yet" />}
          {q.label}
          {q.optional && <span className="text-[13px] font-normal text-slate-400">optional</span>}
        </div>
        {q.hint && (!q.hintWhen || q.hintWhen.some((t) => tags.has(t))) && <p className="mt-0.5 text-[13px] leading-snug text-slate-500">{q.hint}</p>}
        {!fits && <p className="mt-0.5 text-[13px] leading-snug text-slate-500">Doesn&apos;t fit this equipment</p>}
      </div>

      <div className="min-w-0">
        {q.text ? (
          q.key === "comments" ? (
            <textarea
              value={raw}
              disabled={disabled}
              onChange={(e) => onSet(q.key, e.target.value, 900)}
              onBlur={onBlur}
              rows={2}
              placeholder={q.note}
              aria-label={q.label}
              className={cn(inputCls, "h-auto w-full py-2 leading-relaxed")}
            />
          ) : (
            <input
              value={raw}
              disabled={disabled}
              onChange={(e) => onSet(q.key, e.target.value, 900)}
              onBlur={onBlur}
              placeholder={q.note}
              aria-label={q.label}
              className={cn(inputCls, "w-full")}
            />
          )
        ) : (
          <div className="flex flex-wrap items-center gap-2">
            <div role="group" aria-label={q.label} className="flex flex-wrap gap-1.5">
              {(q.options ?? []).map((o) => {
                const sel = pick === o;
                return (
                  <button
                    key={o}
                    type="button"
                    disabled={disabled}
                    aria-pressed={sel}
                    onClick={() => onSet(q.key, saved === o ? "" : o, 150)}
                    className={cn(
                      "inline-flex items-center gap-1.5 rounded-full font-semibold transition-colors",
                      "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 disabled:cursor-not-allowed",
                      phone ? "h-11 px-4 text-[15px] [touch-action:manipulation]" : "h-9 px-3.5 text-[14px]",
                      sel
                        ? phone ? "bg-slate-900 text-white shadow-sm" : "bg-brand-600 text-white shadow-sm hover:bg-brand-700"
                        : phone ? "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 active:bg-slate-100"
                        : "bg-white text-slate-700 ring-1 ring-inset ring-slate-200 hover:bg-brand-50 hover:text-brand-800 hover:ring-brand-300",
                    )}
                  >
                    {sel && fromEquipment && <Sparkles className="h-3.5 w-3.5" aria-hidden />}
                    {o}
                  </button>
                );
              })}
              {legacyPick && (
                <button
                  type="button"
                  disabled={disabled}
                  aria-pressed
                  title="Saved before the choices changed — pick one of the choices, or tap to clear"
                  onClick={() => onSet(q.key, "", 150)}
                  className={cn("inline-flex items-center rounded-full bg-slate-700 font-semibold text-white", phone ? "h-11 px-4 text-[15px]" : "h-9 px-3.5 text-[14px]")}
                >
                  {legacyPick}
                </button>
              )}
            </div>
            {needsAmount && (
              <label className="inline-flex items-center gap-1.5">
                <input
                  value={amt}
                  disabled={disabled}
                  inputMode="decimal"
                  onChange={(e) => onSet(`${q.key}_amt`, e.target.value.replace(/[^\d.]/g, ""), 900)}
                  onBlur={onBlur}
                  placeholder="How many"
                  aria-label={`${q.label} — how many ${q.unit ?? ""}`}
                  className={cn(inputCls, "w-28 text-right tabular-nums", !amt && "ring-1 ring-amber-300")}
                />
                <span className="text-[14px] font-medium text-slate-500">{q.unit}</span>
              </label>
            )}
            {showNote && (
              <input
                value={note}
                disabled={disabled}
                onChange={(e) => onSet(`${q.key}_note`, e.target.value, 900)}
                onBlur={onBlur}
                placeholder={q.note}
                aria-label={`${q.label} — detail`}
                className={cn(inputCls, "min-w-[200px] flex-1")}
              />
            )}
          </div>
        )}
        {fromEquipment && (
          <p className="mt-1 inline-flex items-center gap-1 text-[13px] text-slate-500">
            <Sparkles className="h-3.5 w-3.5 text-brand-500" aria-hidden />
            {q.key === "furnace_eff"
              ? `From the furnace's ${facts.furnaceEffFrom === "pricebook" ? "pricebook rating" : "model number"}`
              : "From the equipment on the job"}
          </p>
        )}
        {effClash && (
          <p className="mt-1 flex items-center gap-1.5 text-[13px] font-medium text-amber-700">
            <AlertCircle className="h-3.5 w-3.5 shrink-0" />
            The furnace on the job reads {facts.furnaceEffGuess === "80" ? "80%" : "90%+"} — double-check this.
          </p>
        )}
      </div>
    </div>
  );
}
